import { StyledDialogContent } from '@/components/decisionSupport/DecisionSupport.styled';
import { RecommendationsTable } from '@/components/decisionSupport/tables/RecommendationsTable';
import { Button, DialogActions } from '@mui/material';
import { type ColumnDef } from '@tanstack/react-table';
import { ComponentType, Fragment, useCallback, useEffect, useRef, useState } from 'react';

type LegacyDecisionSupportRecommendationsProps<T> = {
    getValueFromRecommendation: (recommendation: T | null) => unknown;
    selection: T | null;
    onSubmit: (selection: T | null) => void;
    recommendations: T[];
    columns: ColumnDef<T>[];
    Actions?: ComponentType;
};

export const LegacyDecisionSupportRecommendations = <T,>(
    props: LegacyDecisionSupportRecommendationsProps<T>,
) => {
    const {
        columns,
        recommendations,
        selection,
        onSubmit,
        getValueFromRecommendation,
        Actions = Fragment,
    } = props;

    const [localSelection, setLocalSelection] = useState(selection);
    const initialSelected = useRef(localSelection);

    useEffect(() => {
        initialSelected.current = selection;
        setLocalSelection(selection);
    }, [selection]);

    const selectedValue = getValueFromRecommendation(localSelection);

    const handleSelect = useCallback((rowData: T) => {
        setLocalSelection(rowData);
    }, []);

    const handleSubmit = useCallback(() => {
        onSubmit(localSelection);
        initialSelected.current = localSelection;
    }, [localSelection, onSubmit]);

    return (
        <>
            <StyledDialogContent>
                <RecommendationsTable
                    data={recommendations}
                    columns={columns}
                    selectedValue={selectedValue}
                    getRowValue={getValueFromRecommendation as (row: T) => unknown}
                    onRowSelect={handleSelect}
                />
            </StyledDialogContent>
            <DialogActions>
                <Actions />
                <Button
                    color='primary'
                    onClick={handleSubmit}
                    disabled={
                        getValueFromRecommendation(initialSelected.current) ===
                        getValueFromRecommendation(localSelection)
                    }
                >
                    Confirm
                </Button>
            </DialogActions>
        </>
    );
};
